import React from 'react';
import { History, Pencil, Sprout, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import { ExportMenu, ScreenHeader, EmptyMeter } from '@/components/app/kit.jsx';
import { useOps } from '@/hooks/useOps.jsx';
import { savePrice, seedDefaultPrices, saveFxRate, effectivePrice } from '@/lib/ops-data.js';
import { DEFAULT_PRICES, SEED_EFFECTIVE_FROM } from '@/lib/default-prices.js';
import { fmtMoney, todayIso } from '@/lib/format.js';
import { downloadCsv, downloadTxtReport } from '@/lib/export.js';
import { LoadingBody, ErrorBody } from './Overview.jsx';

// Pricing — every priceKey with its CURRENT effective rate, full history popover,
// and an effective-dated "Edit rate" dialog that CREATES a new ChargeItemDefinition
// (contract §3 — never mutates old entries, so history recomputes identically
// forever). "Seed defaults" creates initial entries from the built-in public-pricing
// map (default-prices.js), each marked "unverified — confirm against your contracts"
// until edited. The FX-rate editor lives here too (§4).
const BASES = ['per-1m', 'per-minute', 'per-1k', 'per-unit'];
const CURRENCIES = ['USD', 'INR', 'EUR'];

export default function Pricing({ range }) {
  const ops = useOps();
  const [editing, setEditing] = React.useState(null); // { priceKey, title, current }
  const [busy, setBusy] = React.useState(false);

  if (ops.loading) return <LoadingBody />;
  if (ops.error) return <ErrorBody error={ops.error} />;

  const today = todayIso();
  const priceList = ops.prices.list;
  const byKey = ops.byKey;

  // One row per priceKey: current effective entry + history. Also surface keys that
  // appear in usage but have NO price at all (the actionable gap).
  const keys = [...byKey.keys()].sort();
  const usedKeys = new Set(
    ops.enriched.filter((r) => r.source === 'metered' && r.metric !== 'billed-cost')
      .map((r) => `${r.provider}:${r.product}:${r.metric}`),
  );
  const missingKeys = [...usedKeys].filter((k) => !byKey.has(k)).sort();

  const rows = keys.map((k) => {
    const history = byKey.get(k) || [];
    const current = effectivePrice(byKey, k, today) || history[history.length - 1];
    return { priceKey: k, current, history: [...history].reverse() };
  });

  const doSeed = async () => {
    setBusy(true);
    try {
      const created = await seedDefaultPrices(
        DEFAULT_PRICES
          .filter((d) => !byKey.has(d.priceKey)) // never duplicate an existing key
          .map((d) => ({ ...d, effectiveFrom: SEED_EFFECTIVE_FROM })),
      );
      if (created.length === 0) toast.info('All default price keys already exist — nothing seeded.');
      else toast.success(`Seeded ${created.length} default prices (marked unverified).`);
      ops.reload();
    } catch (e) {
      toast.error(`Seeding failed: ${e?.message || e}`);
    } finally { setBusy(false); }
  };

  const csv = () => downloadCsv(
    `ops-prices-${today}.csv`,
    [
      { key: 'priceKey', label: 'Price key' }, { key: 'title', label: 'Title' },
      { key: 'rate', label: 'Current rate' }, { key: 'basis', label: 'Basis' },
      { key: 'since', label: 'Effective since' }, { key: 'versions', label: 'Versions' },
    ],
    rows.map((r) => ({
      priceKey: r.priceKey, title: r.current?.title || '',
      rate: r.current ? `${r.current.amount} ${r.current.currency}` : 'NONE',
      basis: r.current?.basis || '', since: r.current?.effectiveFrom || '', versions: r.history.length,
    })),
  );
  const txt = () => downloadTxtReport(`ops-prices-${today}.txt`, {
    title: 'Healther Ops — Price book',
    meta: [`As of: ${today}`, `Price keys: ${rows.length}`, `Missing (used but unpriced): ${missingKeys.length}`],
    sections: [
      {
        heading: 'Current effective rates',
        columns: [
          { key: 'priceKey', label: 'Key' }, { key: 'rate', label: 'Rate' },
          { key: 'basis', label: 'Basis' }, { key: 'since', label: 'Since' },
        ],
        rows: rows.map((r) => ({
          priceKey: r.priceKey,
          rate: r.current ? `${fmtMoney(r.current.amount, r.current.currency)}` : 'NONE',
          basis: r.current?.basis || '', since: r.current?.effectiveFrom || '',
        })),
      },
      ...(missingKeys.length ? [{ heading: 'Used but unpriced', lines: missingKeys }] : []),
    ],
  });

  return (
    <div className="p-6 cc-enter">
      <ScreenHeader title="Pricing" subtitle="Effective-dated price book — edits create new entries, history never mutates">
        <Button variant="outline" size="sm" onClick={doSeed} disabled={busy}>
          <Sprout className="size-3.5" /> Seed defaults
        </Button>
        <ExportMenu onCsv={csv} onTxt={txt} />
      </ScreenHeader>

      {missingKeys.length > 0 && (
        <Card className="mb-4 gap-2 border-[hsl(var(--ai-border))] bg-[hsl(var(--ai-bg))]/40 py-4">
          <CardContent className="px-4">
            <div className="text-[13px] font-semibold text-[hsl(var(--ai-foreground))]">
              {missingKeys.length} price key{missingKeys.length === 1 ? '' : 's'} in use with no rate on file
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {missingKeys.map((k) => (
                <Button key={k} variant="outline" size="xs" className="font-mono text-[11px]"
                  onClick={() => setEditing({ priceKey: k, title: k, current: null })}>
                  <Plus className="size-3" /> {k}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {rows.length === 0 && missingKeys.length === 0 ? (
        <EmptyMeter title="No prices yet" sub='Click "Seed defaults" to create the initial price book from current public pricing, then confirm each rate against your contracts.' />
      ) : (
        <Card className="gap-0 overflow-hidden py-0">
          <CardHeader className="border-b py-4">
            <CardTitle className="text-[13.5px]">Price book</CardTitle>
            <CardDescription className="text-[11.5px]">
              {rows.length} price keys · seeded entries stay "unverified" until you set a rate yourself
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-4">Title</TableHead>
                    <TableHead>Price key</TableHead>
                    <TableHead className="text-right">Current rate</TableHead>
                    <TableHead>Basis</TableHead>
                    <TableHead>Effective since</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="pr-4 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.priceKey}>
                      <TableCell className="pl-4 font-medium text-foreground">{r.current?.title || r.priceKey}</TableCell>
                      <TableCell className="font-mono text-[11.5px] text-muted-foreground">{r.priceKey}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium text-foreground">
                        {r.current ? fmtMoney(r.current.amount, r.current.currency) : '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{r.current?.basis}</TableCell>
                      <TableCell className="tabular-nums text-muted-foreground">{r.current?.effectiveFrom}</TableCell>
                      <TableCell>
                        {r.current?.unverified ? (
                          <Badge variant="outline"
                            title="Seeded from public pricing — confirm against your contracts, then edit the rate to confirm"
                            className="h-5 border-[hsl(var(--ai-border))] bg-[hsl(var(--ai-bg))] px-1.5 text-[10px] font-semibold text-[hsl(var(--ai-foreground))]">
                            unverified — confirm against your contracts
                          </Badge>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">confirmed</span>
                        )}
                      </TableCell>
                      <TableCell className="pr-4 text-right">
                        <div className="inline-flex items-center gap-1">
                          <HistoryPopover history={r.history} />
                          <Button variant="outline" size="xs"
                            onClick={() => setEditing({ priceKey: r.priceKey, title: r.current?.title || r.priceKey, current: r.current })}>
                            <Pencil className="size-3" /> Edit rate
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <FxCard fx={ops.fx} reload={ops.reload} />

      {editing && (
        <EditRateDialog
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); ops.reload(); }}
        />
      )}
    </div>
  );
}

function HistoryPopover({ history }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="xs" title="Rate history"><History className="size-3" /> {history.length}</Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="border-b px-3 py-2 text-[12px] font-semibold">Rate history (newest first)</div>
        <div className="max-h-56 overflow-y-auto">
          {history.map((h) => (
            <div key={h.id} className="flex items-center justify-between px-3 py-2 text-[12px]">
              <span className="tabular-nums text-muted-foreground">{h.effectiveFrom}</span>
              <span className="tabular-nums font-medium text-foreground">{fmtMoney(h.amount, h.currency)} <span className="text-muted-foreground">{h.basis}</span></span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function EditRateDialog({ editing, onClose, onSaved }) {
  const cur = editing.current;
  const [amount, setAmount] = React.useState(cur ? String(cur.amount) : '');
  const [currency, setCurrency] = React.useState(cur?.currency || 'USD');
  const [basis, setBasis] = React.useState(cur?.basis || BASES[0]);
  const [title, setTitle] = React.useState(editing.title || editing.priceKey);
  const [effectiveFrom, setEffectiveFrom] = React.useState(todayIso());
  const [busy, setBusy] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt < 0) { toast.error('Enter a valid non-negative amount.'); return; }
    if (!effectiveFrom) { toast.error('Pick an effective-from date.'); return; }
    setBusy(true);
    try {
      await savePrice({ priceKey: editing.priceKey, title, amount: amt, currency, basis, effectiveFrom });
      toast.success(`New rate for ${editing.priceKey} effective ${effectiveFrom}.`);
      onSaved();
    } catch (err) {
      toast.error(`Could not save rate: ${err?.message || err}`);
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit rate</DialogTitle>
          <DialogDescription>
            <span className="font-mono text-[11.5px]">{editing.priceKey}</span> — saving creates a NEW
            effective-dated entry; earlier days keep computing on the old rate.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-3.5">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pr-title">Title</Label>
            <Input id="pr-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pr-amt">Amount</Label>
              <Input id="pr-amt" type="number" step="any" min="0" value={amount}
                onChange={(e) => setAmount(e.target.value)} placeholder="0.0077" autoFocus />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Basis</Label>
              <Select value={basis} onValueChange={setBasis}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>{BASES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pr-eff">Effective from</Label>
              <Input id="pr-eff" type="date" value={effectiveFrom} max={todayIso()}
                onChange={(e) => setEffectiveFrom(e.target.value)} className="tabular-nums" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Create rate'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// FX-rate editor (Basic fx-rate, §4). Adds a new effective-dated rate; totals
// convert per-day using the rate effective for that day.
function FxCard({ fx, reload }) {
  const [rate, setRate] = React.useState('');
  const [effectiveFrom, setEffectiveFrom] = React.useState(todayIso());
  const [busy, setBusy] = React.useState(false);

  const rates = [...fx].sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1));

  const submit = async (e) => {
    e.preventDefault();
    const r = Number(rate);
    if (!Number.isFinite(r) || r <= 0) { toast.error('Enter a valid USD→INR rate.'); return; }
    setBusy(true);
    try {
      await saveFxRate({ base: 'USD', quote: 'INR', rate: r, effectiveFrom });
      toast.success(`USD→INR ${r} effective ${effectiveFrom}.`);
      setRate('');
      reload();
    } catch (err) {
      toast.error(`Could not save FX rate: ${err?.message || err}`);
    } finally { setBusy(false); }
  };

  return (
    <Card className="mt-4 gap-0 overflow-hidden py-0">
      <CardHeader className="border-b py-4">
        <CardTitle className="text-[13.5px]">FX rates (USD → INR)</CardTitle>
        <CardDescription className="text-[11.5px]">
          Display totals convert per-day at the effective rate; raw per-currency subtotals always stay visible
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 p-4 lg:flex-row">
        <form onSubmit={submit} className="flex flex-1 flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fx-rate">Rate (₹ per $1)</Label>
            <Input id="fx-rate" type="number" step="any" min="0" className="w-32 tabular-nums"
              value={rate} onChange={(e) => setRate(e.target.value)} placeholder="86.40" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fx-eff">Effective from</Label>
            <Input id="fx-eff" type="date" className="w-40 tabular-nums" value={effectiveFrom}
              max={todayIso()} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </div>
          <Button type="submit" disabled={busy}><Plus className="size-4" /> Add rate</Button>
        </form>
        <div className="flex-1">
          {rates.length === 0 ? (
            <div className="text-[12.5px] text-muted-foreground">
              No FX rate on file — USD spend cannot be shown in INR until one is added.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pair</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Effective from</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rates.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.base} → {r.quote}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.rate}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{r.effectiveFrom}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
